/**
 * The matched fragment of a message body, ready to render — pure.
 *
 * A search row shows WHY a message matched, which means the term has to be visible and
 * marked inside a body that may be a thousand characters long. Both halves of that —
 * which window of the text to show, and where the term sits inside it — are one
 * calculation, so they live here rather than being re-derived in JSX.
 *
 * ## Only the first occurrence is marked
 *
 * The server matches with `ILIKE '%term%'` and says nothing about where. Marking every
 * occurrence turns a search for "the" into a stripe; the first is the one the window is
 * centred on, and the one the reader is looking for.
 *
 * ## `indexOf`, never `RegExp`
 *
 * The term is raw user input. `a.*b`, `(` and `\` are all legal things to search a
 * thread for, and every one of them is either a different match or a thrown
 * `SyntaxError` once it reaches a `RegExp` constructor.
 */

/** One run of body text, flagged as the matched term or as context around it. */
export type SnippetSegment = {
  text: string;
  match: boolean;
};

/**
 * Characters of context kept BEFORE the match. A result row is ONE clamped line and about
 * 50 characters fit in its 350px text column (measured), so the lead has to leave room for
 * the mark itself — a generous one pushes the marked term off the right edge and the row
 * shows an ellipsis and no highlight at all. At 28 the mark lands around column 29, and
 * any match inside the first 28 characters is shown from the body's first word.
 */
const SNIPPET_HEAD_RADIUS = 28;

/** Characters kept AFTER it. Generous — CSS truncation decides where the line really ends. */
const SNIPPET_TAIL_RADIUS = 160;

/** Hard cap for a body with no locatable match (an attachment-only message, say). */
const SNIPPET_MAX = 220;

function clamp(text: string): string {
  return text.length > SNIPPET_MAX ? `${text.slice(0, SNIPPET_MAX)}…` : text;
}

/**
 * Builds the renderable segments for one search result row.
 *
 * @param body - The message's raw markdown body. Null for a message that is only
 *   attachments — the caller still renders a row, just without a snippet.
 * @param term - The term the user typed, untrimmed.
 * @returns Segments in order; concatenating `text` gives the visible snippet. Empty
 *   when there is nothing to show at all.
 */
export function buildMatchSnippet(
  body: string | null | undefined,
  term: string,
): SnippetSegment[] {
  // Markdown's newlines and list bullets read as noise on one clamped line.
  const text = (body ?? "").replace(/\s+/g, " ").trim();
  if (text === "") return [];

  const needle = term.trim();
  if (needle === "") return [{ text: clamp(text), match: false }];

  const at = text.toLowerCase().indexOf(needle.toLowerCase());
  // The server matched on `body`; this runs on the whitespace-collapsed copy, so a term
  // spanning a newline can legitimately miss here. Show the head of the body instead.
  if (at < 0) return [{ text: clamp(text), match: false }];

  const start = Math.max(0, at - SNIPPET_HEAD_RADIUS);
  const end = Math.min(text.length, at + needle.length + SNIPPET_TAIL_RADIUS);

  const head = `${start > 0 ? "…" : ""}${text.slice(start, at)}`;
  const tail = `${text.slice(at + needle.length, end)}${end < text.length ? "…" : ""}`;

  return [
    { text: head, match: false },
    { text: text.slice(at, at + needle.length), match: true },
    { text: tail, match: false },
  ].filter((segment) => segment.text !== "");
}
